import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import RoleMasterService, { RoleFormPayload } from '../../../../services/role-master.service';
import { Modules, AuthRoutes } from '../../../../enums';

const initialForm: RoleFormPayload = {
  code: '',
  name: '',
  permissions: [],
  scope: 'product',
  notes: '',
};

const RoleForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<RoleFormPayload>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);

  useEffect(() => {
    RoleMasterService.listPermissionKeys().then((response: any) => {
      setAllPermissions(response?.data ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    RoleMasterService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          code: row.code ?? '',
          name: row.name ?? '',
          permissions: row.permissions ?? [],
          scope: row.scope ?? 'product',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: RoleFormPayload) => {
      const nextErrors: Record<string, string> = {};
      if (!data.code.trim()) nextErrors.code = t('masters.roles.form.pageTexts.fields.code.errors.required');
      if (!data.name.trim()) nextErrors.name = t('masters.roles.form.pageTexts.fields.name.errors.required');
      if (!data.permissions.length) nextErrors.permissions = t('masters.roles.form.pageTexts.fields.permissions.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  const handlePermissionToggle = useCallback((key: string) => {
    setFormData((prev) => {
      const has = prev.permissions.includes(key);
      return {
        ...prev,
        permissions: has ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key],
      };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        if (edit && id) {
          await RoleMasterService.update(id, formData);
          toast.success(<Toast message={t('masters.roles.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await RoleMasterService.create(formData);
          toast.success(<Toast message={t('masters.roles.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.roles}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.roles.form.pageTexts.editTitle') : t('masters.roles.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="code"
            name="code"
            label={t('masters.roles.form.pageTexts.fields.code.label')}
            placeholder={t('masters.roles.form.pageTexts.fields.code.placeholder')}
            required
            value={formData.code}
            error={!!errors.code}
            errorMessage={errors.code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <Input
            id="name"
            name="name"
            label={t('masters.roles.form.pageTexts.fields.name.label')}
            placeholder={t('masters.roles.form.pageTexts.fields.name.placeholder')}
            required
            value={formData.name}
            error={!!errors.name}
            errorMessage={errors.name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label d-block">{t('masters.roles.form.pageTexts.fields.scope.label')}</label>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="scope"
              id="scope-global"
              checked={formData.scope === 'global'}
              onChange={() => setFormData((prev) => ({ ...prev, scope: 'global' }))}
            />
            <label className="form-check-label" htmlFor="scope-global">
              {t('masters.roles.form.pageTexts.fields.scope.options.global')}
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="scope"
              id="scope-product"
              checked={formData.scope === 'product'}
              onChange={() => setFormData((prev) => ({ ...prev, scope: 'product' }))}
            />
            <label className="form-check-label" htmlFor="scope-product">
              {t('masters.roles.form.pageTexts.fields.scope.options.product')}
            </label>
          </div>
        </div>
        <div className="col-12">
          <label className="form-label d-block">{t('masters.roles.form.pageTexts.fields.permissions.label')}</label>
          <div className="row">
            {allPermissions.map((key) => (
              <div className="col-md-3 col-sm-4 col-6" key={key}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`perm-${key}`}
                    checked={formData.permissions.includes(key)}
                    onChange={() => handlePermissionToggle(key)}
                  />
                  <label className="form-check-label" htmlFor={`perm-${key}`}>
                    {key}
                  </label>
                </div>
              </div>
            ))}
          </div>
          {errors.permissions && <div className="text-danger mt-1">{errors.permissions}</div>}
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.roles.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.roles.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.roles.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.roles}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.roles.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default RoleForm;
